import { useState } from 'react';
import { uploadFile } from '../../../api/fileApi';
import { downscaleImage, RESIZE_PRESETS } from '../../../shared/utils/imageResize';
import FadeImage from '../../../shared/components/FadeImage';
import TalentPicker from './TalentPicker';

/**
 * 하위 목록 공용 편집기.
 *
 * 필모그래피·소속 배우·학과·혜택·갤러리는 필드만 다르고 조작(추가·수정·순서·삭제)이 같다.
 * 목록마다 화면을 따로 만들면 다섯 벌을 유지해야 하므로 스키마로 구동한다.
 *
 * @param {{ key, label, type?: 'text'|'number'|'textarea'|'image'|'talent', placeholder?, width?, hint?, nameKey? }[]} fields
 */
export default function ItemListEditor({ title, description, fields, items, onChange, addLabel = '항목 추가' }) {
  const [uploadingIdx, setUploadingIdx] = useState(null);

  const update = (idx, key, value) =>
    onChange(items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    [next[idx], next[to]] = [next[to], next[idx]];
    onChange(next);
  };

  const add = () => {
    const blank = {};
    fields.forEach(f => { blank[f.key] = f.type === 'image' ? null : ''; });
    onChange([...items, blank]);
  };

  const handleUpload = async (idx, field, file) => {
    if (!file) return;
    setUploadingIdx(`${idx}-${field.key}`);
    try {
      // 카드 썸네일로만 쓰이므로 원본 그대로 올릴 이유가 없다
      const sized = await downscaleImage(file, RESIZE_PRESETS.thumbnail);
      const { fileEntityId, url } = await uploadFile(sized, 'IMAGE');
      onChange(items.map((it, i) => (
        i === idx ? { ...it, [field.key]: fileEntityId, [field.urlKey]: url } : it
      )));
    } catch (err) {
      window.alert(err.message || '이미지 업로드에 실패했습니다.');
    } finally {
      setUploadingIdx(null);
    }
  };

  const renderField = (item, idx, f) => {
    if (f.type === 'image') {
      const busy = uploadingIdx === `${idx}-${f.key}`;
      const url = item[f.urlKey];
      return (
        <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: f.width ?? '1 1 200px' }}>
          <div style={{
            width: 52, height: 52, borderRadius: 8, flexShrink: 0, overflow: 'hidden',
            border: '1px solid var(--color-border)', background: 'var(--color-background)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {url
              ? <FadeImage src={url} alt="" size="medium" background="transparent" />
              : <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>없음</span>}
          </div>
          <label className="ag-btn ag-btn--sm" style={{ cursor: busy ? 'default' : 'pointer' }}>
            <i className="ti ti-upload" /> {busy ? '업로드 중' : url ? '변경' : f.label}
            <input
              type="file" accept="image/*" style={{ display: 'none' }} disabled={busy}
              onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; handleUpload(idx, f, file); }}
            />
          </label>
        </div>
      );
    }

    if (f.type === 'talent') {
      return (
        <div key={f.key} style={{ flex: f.width ?? '1 1 220px', minWidth: 200 }}>
          <TalentPicker
            value={item[f.key]}
            name={item[f.nameKey ?? 'name']}
            onChange={(id, actor) => {
              // 이름 칸이 비어 있으면 고른 배우 이름으로 채워준다
              const patch = { [f.key]: id };
              const nameKey = f.nameKey ?? 'name';
              if (actor?.name && !String(item[nameKey] ?? '').trim()) patch[nameKey] = actor.name;
              onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
            }}
          />
          {f.hint && (
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>{f.hint}</div>
          )}
        </div>
      );
    }

    if (f.type === 'textarea') {
      return (
        <textarea
          key={f.key}
          className="ag-field__input"
          value={item[f.key] ?? ''}
          onChange={(e) => update(idx, f.key, e.target.value)}
          placeholder={f.placeholder ?? f.label}
          rows={2}
          style={{ flex: f.width ?? '1 1 100%', resize: 'vertical', fontFamily: 'inherit' }}
        />
      );
    }

    const input = (
      <input
        className="ag-field__input"
        type={f.type === 'number' ? 'number' : 'text'}
        value={item[f.key] ?? ''}
        onChange={(e) => update(idx, f.key, e.target.value)}
        placeholder={f.placeholder ?? f.label}
        style={{ width: '100%' }}
      />
    );

    if (!f.hint) {
      return (
        <div key={f.key} style={{ flex: f.width ?? '1 1 160px', minWidth: 120 }}>
          {input}
        </div>
      );
    }

    return (
      <div key={f.key} style={{ flex: f.width ?? '1 1 160px', minWidth: 120 }}>
        {input}
        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>{f.hint}</div>
      </div>
    );
  };

  return (
    <div className="ag-card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{title} <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400 }}>{items.length}</span></div>
          {description && (
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4 }}>{description}</div>
          )}
        </div>
        <button type="button" className="ag-btn ag-btn--sm" onClick={add}>
          <i className="ti ti-plus" /> {addLabel}
        </button>
      </div>

      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', padding: '12px 0' }}>
          등록된 항목이 없습니다.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map((item, idx) => (
            <div
              key={item.id ?? `new-${idx}`}
              style={{
                display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap',
                padding: 12, border: '1px solid var(--color-border)', borderRadius: 10,
              }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 8,
                background: 'var(--color-background)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: 'var(--color-text-tertiary)',
              }}>
                {idx + 1}
              </span>

              {fields.map(f => renderField(item, idx, f))}

              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button type="button" className="ag-btn ag-btn--sm" disabled={idx === 0} onClick={() => move(idx, -1)} title="위로">↑</button>
                <button type="button" className="ag-btn ag-btn--sm" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} title="아래로">↓</button>
                <button type="button" className="ag-btn ag-btn--sm ag-btn--danger" onClick={() => remove(idx)}>삭제</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
